import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, TriangleAlert } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const STATUS_TONE = {
  Queued: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  Pulling: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  Pulled: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200",
  "Out for delivery": "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200",
  Delivered: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};
export const WH_STATUSES = Object.keys(STATUS_TONE);
export const WhStatusChip = ({ status, testId }) => (
  <span data-testid={testId} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_TONE[status] || STATUS_TONE[String(status).split(" — ")[0]] || STATUS_TONE.Queued}`}>{status}</span>
);

export const fmtDay = (iso, opts = { weekday: "short", month: "short", day: "numeric" }) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", opts);
};
const weekLabel = (iso) => { const [y, m, d] = iso.split("-"); return `${+m}.${+d}.${y.slice(2)}`; };
const DAY = { Mon: "M", Tue: "T", Wed: "W", Thu: "Th", Fri: "F", Sat: "S", Sun: "Su" };

/** `day` in the URL is one date, a comma list of dates, or "week" (all seven). */
export const parseDays = (day, data) => {
  if (!data) return [];
  if (day === "week") return data.days.map((d) => d.date);
  return String(day || "").split(",").filter((d) => data.days.some((x) => x.date === d));
};

/** One load of /warehouse/queue for a week; `day` is kept valid for that week. `selected` merges the chosen days. */
export function usePullQueue(week, day, setDay) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get("/warehouse/queue", { params: week ? { week } : {} })
      .then(({ data: d }) => {
        if (!live) return;
        setData(d);
        if (day !== "week" && parseDays(day, d).length === 0) setDay(d.days.some((x) => x.date === d.default_day) ? d.default_day : d.days[0].date);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load the pull queue"))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [week, tick]);          // eslint-disable-line react-hooks/exhaustive-deps
  const days = useMemo(() => parseDays(day, data), [day, data]);
  const selected = useMemo(() => {
    if (!data || !days.length) return null;
    const picked = data.days.filter((d) => days.includes(d.date));
    const seen = new Set();
    const tickets = [];
    picked.forEach((d) => d.tickets.forEach((c) => { if (!seen.has(c.id)) { seen.add(c.id); tickets.push(c); } }));
    tickets.sort((a, b) => (b.late - a.late) || (b.late_days - a.late_days) || a.delivery_date.localeCompare(b.delivery_date) || a.ticket_number.localeCompare(b.ticket_number));
    return { dates: picked.map((d) => d.date), is_today: picked.some((d) => d.is_today), count: tickets.length, tickets, multi: picked.length > 1 };
  }, [data, days]);
  return { data, loading, days, selected, reload: () => setTick((n) => n + 1) };
}

/** ‹ Week of M.D.YY › + M T W Th F S Su with counts. `inline` lays it out on one line for the desktop strip. */
export function WeekStrip({ data, day, onDay, onWeek, loading, inline = false }) {
  if (!data) return <span className="inline-flex items-center text-xs text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-1" /> queue…</span>;
  const isPast = data.week_end < data.today;
  const nav = (cls) => cls;
  const btn = inline ? "h-8 w-8" : "min-h-[44px] min-w-[44px]";
  const picked = parseDays(day, data);
  const wholeWeek = picked.length === data.days.length;
  // click a day = toggle it (several days at once; unclicking the last leaves the list empty)
  const toggle = (date) => { const next = picked.includes(date) ? picked.filter((d) => d !== date) : [...picked, date].sort(); onDay(next.length === data.days.length ? "week" : next.join(",")); };
  return (
    <div className={inline ? "flex items-center gap-2" : "space-y-3"} data-testid="pull-week-strip">
      <div className={`flex items-center ${inline ? "gap-1" : "justify-between gap-2"}`}>
        <button type="button" onClick={() => onWeek(data.prev_week)} className={nav(`${btn} inline-flex items-center justify-center rounded-full border border-border bg-card text-foreground`)} aria-label="Previous week" data-testid="pull-queue-prev-week"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-center min-w-[7.5rem]">
          <button type="button" onClick={() => onDay(wholeWeek ? (data.days.some((x) => x.date === data.default_day) ? data.default_day : data.days[0].date) : "week")} data-testid="pull-queue-week" data-active={wholeWeek ? "1" : "0"} title={wholeWeek ? "Clear the week" : "Show the whole week"}
                  className={`text-sm font-semibold whitespace-nowrap rounded-md px-2 py-0.5 transition-colors ${wholeWeek ? "bg-foreground text-background" : "text-foreground hover:bg-muted"}`}>Week of {weekLabel(data.week_start)}</button>
          {!inline && <div className="text-[11px] text-muted-foreground">{isPast ? "History — delivered, read-only" : data.week_start > data.today ? "Scheduled" : "Monday – Sunday"}{loading ? " · loading…" : ""}</div>}
        </div>
        <button type="button" onClick={() => onWeek(data.next_week)} className={nav(`${btn} inline-flex items-center justify-center rounded-full border border-border bg-card text-foreground`)} aria-label="Next week" data-testid="pull-queue-next-week"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className={inline ? "flex items-center gap-1" : "grid grid-cols-7 gap-1"} data-testid="pull-queue-days">
        {data.days.map((d) => {
          const on = picked.includes(d.date);
          const dim = (d.weekday === "Sat" || d.weekday === "Sun") && d.count === 0;
          return (
            <button key={d.date} type="button" onClick={() => toggle(d.date)} data-testid={`pull-queue-day-${d.date}`} data-count={d.count} data-active={on ? "1" : "0"}
                    className={`${inline ? "h-9 min-w-[2.6rem] px-1.5 flex-row gap-1" : "min-h-[56px] flex-col gap-0.5"} rounded-xl border flex items-center justify-center transition-colors ${on ? "bg-foreground text-background border-foreground" : d.is_today ? "border-foreground/60 bg-card text-foreground" : "border-border bg-card text-foreground"} ${dim ? "opacity-40" : ""}`}>
              <span className="text-xs font-semibold">{DAY[d.weekday]}</span>
              <span className={`${inline ? "text-xs" : "text-sm"} tabular-nums font-bold ${on || d.count ? "" : "text-muted-foreground"}`}>{d.count}</span>
              {d.late_count > 0 && <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-red-300" : "bg-red-500"}`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The ticket card — the same card on a phone and in the desktop queue pane (`compact`). */
export function TicketCard({ c, onOpen, compact = false, selected = false, showDay = false }) {
  return (
    <button type="button" onClick={() => onOpen(c)} data-testid={`pull-card-${c.ticket_number}`} data-selected={selected ? "1" : "0"}
            className={`w-full text-left rounded-xl border bg-card ${compact ? "p-2.5" : "p-3"} shadow-sm active:bg-muted/60 transition-colors ${selected ? "border-foreground ring-1 ring-foreground" : "border-border hover:border-foreground/40"}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-semibold text-foreground">{c.job_number || "—"}</span>
            <span className="text-sm font-semibold text-foreground truncate">{c.project_name}</span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">{c.ticket_number} · {c.delivery_type} · {c.items} item{c.items === 1 ? "" : "s"}{c.delivery_date ? <> · <b className="text-foreground font-medium" data-testid={`pull-card-day-${c.ticket_number}`}>Deliver {fmtDay(c.delivery_date)}</b></> : null}{c.pull_kind_label ? <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.pull_kind === "split" ? "bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-200" : "bg-muted text-foreground"}`} data-testid={`pull-card-kind-${c.ticket_number}`}>{c.pull_kind_label}</span> : null}</div>
        </div>
        <WhStatusChip status={c.wh_status} testId={`pull-card-status-${c.ticket_number}`} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${c.progress.pct}%` }} /></div>
        <span className="text-[11px] tabular-nums text-muted-foreground">{c.progress.pulled} of {c.progress.requested}</span>
        {c.issues_open > 0 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200" data-testid={`pull-card-issues-${c.ticket_number}`}><TriangleAlert className="w-3 h-3" /> {c.issues_open} issue{c.issues_open === 1 ? "" : "s"}</span>}
      </div>
      {c.late && <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-600 text-white" data-testid={`pull-card-late-${c.ticket_number}`}>Late — from {fmtDay(c.key_date || c.delivery_date, { month: "short", day: "numeric", year: "numeric" })} · {c.late_days} day{c.late_days === 1 ? "" : "s"}</div>}
    </button>
  );
}

export const dayHeading = (sel) => !sel ? "" : sel.dates.length === 7 ? "Whole week" : sel.dates.length > 1 ? `${sel.dates.length} days` : `${sel.is_today ? "Today · " : ""}${fmtDay(sel.dates[0], { weekday: "long", month: "short", day: "numeric" })}`;
export const EmptyDay = () => <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="pull-queue-empty">Nothing to pull for this day.</div>;

/** WH-PULL-1 §2 — the phone queue: week strip stacked over the day's cards. */
export default function PullQueue({ week, day, onWeek, onDay, onOpen }) {
  const { data, loading, selected: sel } = usePullQueue(week, day, onDay);
  if (!data) return <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  return (
    <div className="space-y-3 px-2 sm:px-0" data-testid="pull-queue">
      <WeekStrip data={data} day={day} onDay={onDay} onWeek={onWeek} loading={loading} />
      {!sel && <EmptyDay />}
      {sel && (
        <div className="space-y-2" data-testid="pull-queue-cards">
          <div className="text-xs text-muted-foreground px-1">{dayHeading(sel)} · {sel.count} ticket{sel.count === 1 ? "" : "s"}</div>
          {sel.tickets.length === 0 && <EmptyDay />}
          {sel.tickets.map((c) => <TicketCard key={c.id} c={c} onOpen={onOpen} showDay={sel.multi} />)}
        </div>
      )}
    </div>
  );
}
